import React, { useState, useEffect, useRef, useContext, Suspense } from 'react';
import IconButton from "@mui/material/IconButton";
import {Key, PublishedWithChanges, Save} from "@mui/icons-material";
import Tabs from "@mui/material/Tabs";
import Fullscreen from '@mui/icons-material/Fullscreen';
import FullscreenExit from '@mui/icons-material/FullscreenExit';
import PlayArrow from '@mui/icons-material/PlayArrow';
import { colorBox, colorGray, monacoTheme } from "../App/theme.js";
import { convertHexToRgba, isMobile } from "../App/utils.js";
import { FilesEditor } from "./FilesEditor.js";
import CircularProgress from '@mui/material/CircularProgress';
import { CodeTabIsLoading, MonacoOptions, getContainerStyles, getFilenameByKey } from "./CodeEditorUtils.js";
import { CreateCodeFilePrompt } from "./CreateCodeFilePrompt.js";
import { VersionsList } from "./VersionsList";
import { AlertContext } from "../App/App.js";
import Tooltip from "@mui/material/Tooltip";
import { Avatar } from "@mui/material";
import { SecretsList } from "./SecretsList";
import MenuIcon from "@mui/icons-material/Menu";
import LocalLoader from "../Loaders/LocalLoader";
import {
    createHandleBlueprintSelect, createHandleKeyDown,
    createHandleSave, createSelectFileByKey, createSelectLeft, createSelectRight, handleDeploy,
    onFileCreate, onFileDelete, onOpenFile, runCode, sendVersionRestoredAlert
} from "./CodeEditorHelpers";

import { createHandleEditorDidMount } from "./CodeEditorTools";
import { CodeEditorConsole } from "./CodeEditorConsole";
import { renderOpenedTabs } from "./CodeEditorTabs";
import {containsES6SyntaxOrJSX} from "./utils";
import ClickAwayListener from '@mui/material/ClickAwayListener';
import {usePrompt} from "../Components/PromptContext";
import {useConfirm} from "../Components/ConfirmContext";

const LazyEditor = React.lazy(() => import('@monaco-editor/react'));

export function CodeEditor(props) {
    const { withFilesEditor, kbId, KBData, setKBData } = props;

    const saveRef = useRef();
    const deployRef = useRef();
    const playRef = useRef();
    const [isFullscreen, setFullscreen] = useState(false);
    const [response, setResponse] = useState(null);
    const [codeLoading, setCodeLoading] = useState(false);
    const [codeRunLoading, setCodeRunLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [fileItemToCreate, setFileItemToCreate] = useState(null);
    const [filesData, setFilesData] = useState([]);
    const [filepathVersionsOpen, setFilepathVersionsOpen] = useState(false);
    const [saveTooltipOpen, setSaveTooltipOpen] = useState(false);
    const [deployTooltipOpen, setDeployTooltipOpen] = useState(false);
    const [secretsOpened, setSecretsOpened] = useState(false);
    const [mobileMenu, setMobileMenu] = useState(true);
    const [loading, setLoading] = useState(false);
    const [blockingLoading, setBlockingLoading] = useState(false);
    const [codeGenerationLoading, setCodeGenerationLoading] = useState(false);

    const [loadedFiles, setLoadedFiles] = useState(() => {
        const savedFiles = localStorage.getItem('loadedFiles');
        return savedFiles ? JSON.parse(savedFiles) : [];
    });

    const [editors, setEditors] = useState({});
    const [diffs, setDiffs] = useState({});
    const codeAbortController = useRef(null);
    const [handleEditorDidMount, setHandleEditorDidMount] = useState(null);
    const [updateDiffsFunctions, setUpdateDiffsFunctions] = useState({});

    const showPrompt = usePrompt();
    const showConfirm = useConfirm();
    useEffect(() => {
        const filesToSave = loadedFiles.map(o => ({ ...o, content: undefined }));
        localStorage.setItem('loadedFiles', JSON.stringify(filesToSave))
    }, [loadedFiles]);

    // Reopen each file from the loadedFiles array
    useEffect(() => {
        loadedFiles.forEach(fileItem => {
            onOpenFile({ fileItem, selectFileByKey, loadedFiles, setFileItemToCreate, setLoadedFiles })
        })
    }, []);

    const setContentAndCursor = (v, position) =>
        setLoadedFiles((prev) => prev.map(f =>
            f.selected
                ? { ...f, content: v }
                : f
        ))

    const selectedIndex = loadedFiles.findIndex(f => f.selected);
    const selectedFile = loadedFiles?.[selectedIndex];

    let language = 'javascript';
    if (selectedFile?.Key?.endsWith('contentRender.js')) language = 'javascript';
    if (selectedFile?.Key?.endsWith('.json')) language = 'json';

    const selectTab = (index) => setLoadedFiles(prev => prev.map((f, i) => {
        return { ...f, selected: i === index };
    }));

    const menuRef = useRef(null);
    const [menuWidth, setMenuWidth] = useState(0);

    const menuOffset = (!isMobile && menuRef?.current?.offsetWidth) || 0;

    const selectFileByKey = createSelectFileByKey({ setLoadedFiles });
    const selectRight = createSelectRight({ setLoadedFiles })
    const selectLeft = createSelectLeft({ setLoadedFiles })

    const Alert = useContext(AlertContext);

    useEffect(() => {
        if (menuRef.current) setMenuWidth(menuRef.current.offsetWidth);
    }, []);

    useEffect(() => {
        const onResponse = ({ generatedCode, selectedCode, done }) => {
            let progress = ((generatedCode?.length + 1) / (selectedCode?.length + 1)) * 100;
            if (progress > 99 && !done) progress = 99;
            setCodeGenerationLoading(done ? 0 : progress);
        }
        const newHandleEditorDidMount = createHandleEditorDidMount({
            setEditors,
            selectRight,
            selectLeft,
            onResponse,
            Alert,
            showPrompt
        });

        setHandleEditorDidMount(() => async (...args) => {
            const updateDiffs = await newHandleEditorDidMount(...args);
            setUpdateDiffsFunctions(prev => ({ ...prev, [args[2]]: updateDiffs }));
        });
    }, [setEditors, setLoadedFiles, setCodeGenerationLoading]);

    useEffect(() => {
        const currentURL = new URL(window.location.href);
        const fullscreenParam = currentURL.searchParams.get("fullscreen");
        setFullscreen(fullscreenParam === "true");
    }, []);

    useEffect(() => {
        const currentURL = new URL(window.location.href);
        if (isFullscreen) {
            currentURL.searchParams.set("fullscreen", "true");
        } else {
            currentURL.searchParams.delete("fullscreen");
        }
        window.history.pushState({}, "", currentURL.toString());
    }, [isFullscreen]);

    useEffect(() => {
        if (editors[selectedFile?.Key]) {
            editors[selectedFile.Key].focus();
        }
    }, [loadedFiles, editors, selectedFile]);

    useEffect(() => {
        const handleKeyDown = createHandleKeyDown({ selectRight, selectLeft, saveRef, deployRef, playRef })
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [loadedFiles]);

    const updateFiles = (namespace, data) => {
        setFilesData((prev) => [
            ...prev.filter(o => namespace === 'frontend' ? o.folder !== 'Frontend' : o.folder === 'Frontend'),
            ...data
        ]);
    }

    const selectedModule = selectedFile ? selectedFile?.Key?.split('/')?.[3]?.split('.')[0] : null;
    const selectedFolder = selectedFile ? selectedFile?.Key?.split('/')?.[2] : null;

    let moduleReadyForDeploy = false;
    if (['Events/onRequest', 'Events/onResponse', 'Events/onAddMessages', 'Events/onPublicAPIRequest', 'Frontend/contentRender'].includes(selectedFolder + '/' + selectedModule)) {
        moduleReadyForDeploy = filesData.find(o => o.folder === selectedFolder)?.children?.find(o => o.filename === selectedModule + '.js' && !o.empty);
    }

    const handleManageSecrets = () => {
        setFullscreen(false);
        setSecretsOpened(true);
    }

    const zIndex = 9999;
    const leftMenuWidth = isMobile ? (mobileMenu ? '32vh' : 0) : '32vh';
    const keyPars = selectedFile?.Key?.split('/');
    const isFrontendSelected = keyPars && keyPars[0] === 'frontend' && keyPars[2] === 'Frontend';
    let ActionText = isFrontendSelected ? 'Build and Deploy ...' : 'Running...';
    const saveDisabled = !diffs[selectedFile?.Key]?.length || selectedFile?.tabIsLoading || saving;
    const fileOpenParams = { selectFileByKey, loadedFiles, setFileItemToCreate, setLoadedFiles }

    const handleSave = createHandleSave({
        selectedFile,
        setDeployTooltipOpen,
        setLoadedFiles,
        updateFiles,
        setSaving: setBlockingLoading, // we needed to disable file tab switch during deploy
        setDiffs,
        updateDiffsFunctions,
        fileOpenParams
    });

    const [anchorEl, setAnchorEl] = useState(null);
    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const leftOffset = isMobile ? 40 : 0;

    const executeDisabled = isFrontendSelected || loading || selectedFile?.tabIsLoading
        || containsES6SyntaxOrJSX(selectedFile?.content);

    return (
        <div style={getContainerStyles(isFullscreen)}>
            <LocalLoader blocking={false} open={codeGenerationLoading || loading} />
            <LocalLoader blocking={true} open={blockingLoading} />
            <SecretsList
                {...{ open: secretsOpened, kbId, setKBData, KBData }}
                handleClose={() => setSecretsOpened(false)}
            />
            <VersionsList
                {...{ filepathVersionsOpen, kbId }} handleClose={() => setFilepathVersionsOpen(false)}
                onVersionRestored={(key, version, restoredContent, namespace) => {
                    sendVersionRestoredAlert(Alert)
                    onOpenFile({ reload: false, fileItem: { Key: `${namespace}/${kbId}/${filepathVersionsOpen}` }, selectFileByKey, ...fileOpenParams })
                    setFilepathVersionsOpen(false)
                    const position = editors[selectedFile.Key]?.getPosition();
                    setContentAndCursor(restoredContent, position);
                }}
            />
            <div
                style={{
                    borderBottom: '1px solid',
                    borderBottomColor: convertHexToRgba(colorBox, 0.5),
                    height: '50px',
                    width: 'calc(100% - 50px)'
                }}>
                {loadedFiles.length > 0 && !isMobile &&
                    <Tabs TabIndicatorProps={{ style: { transition: 'none' } }}
                          style={{ marginLeft: '32vh' }}
                          value={selectedIndex}
                          onChange={(event, newValue) => selectTab(newValue)}
                          indicatorColor="primary"
                          textColor="primary"
                          variant="scrollable"
                          scrollButtons="auto"
                    >
                        {renderOpenedTabs({ loadedFiles, setEditors, setLoadedFiles, diffs })}
                    </Tabs>
                }

                <Tooltip title={'Manage Code Secrets'} placement="top" arrow>
                    <IconButton
                        edge="end"
                        color="primary"
                        onClick={() => handleManageSecrets()}
                        style={{ position: 'absolute', top: 7, left: leftOffset + 10, zIndex }}
                    >
                        <Key style={{ fontSize: '1.25rem' }} />
                    </IconButton>
                </Tooltip>

                {selectedFile && <>
                    {<>
                        <Tooltip open={deployTooltipOpen && !codeLoading} title={'Build and Deploy'} placement="top" arrow>
                            <IconButton
                                onMouseLeave={() => setDeployTooltipOpen(false)}
                                onMouseEnter={() => setDeployTooltipOpen(true)}
                                edge="end"
                                color="primary"
                                ref={deployRef}
                                disabled={!moduleReadyForDeploy}
                                onClick={() => handleDeploy({
                                    selectedFile,
                                    loadedFiles,
                                    setCodeLoading: setBlockingLoading, // block the ui against file tab change
                                    setResponse,
                                    handleSave,
                                    updateFiles
                                })}
                                style={{ position: 'absolute', top: 7, left: leftOffset + 130, zIndex }}
                            >
                                {codeLoading ? <CircularProgress size={24} /> : <PublishedWithChanges style={{ fontSize: '1.25rem', color: !moduleReadyForDeploy ? colorGray : undefined }} />}
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={'Run Code'} placement="top" arrow>
                            <IconButton
                                disabled={executeDisabled}
                                ref={playRef}
                                component="span"
                                edge="end"
                                color="primary"
                                onClick={() => runCode({ setCodeRunLoading, selectedFile, KBData, setResponse })}
                                style={{ position: 'absolute', top: 7, left: leftOffset + 50, zIndex }}
                            >
                                {codeRunLoading ?
                                    <CircularProgress size={24} /> :
                                    <PlayArrow style={{ fontSize: '1.25rem', color: executeDisabled ? 'grey' : undefined }} />
                                }
                            </IconButton>
                        </Tooltip>

                        <Tooltip open={saveTooltipOpen && !saveDisabled} title={'Save'} placement="top" arrow>
                            <IconButton
                                onMouseEnter={() => setSaveTooltipOpen(true)}
                                onMouseLeave={() => setSaveTooltipOpen(false)}
                                ref={saveRef}
                                edge="end"
                                color="secondary"
                                disabled={saveDisabled}
                                onClick={(e) => {
                                    e?.preventDefault();
                                    handleSave({});
                                }}
                                style={{ position: 'absolute', top: 7, left: leftOffset + 90, zIndex }}
                            >
                                {(saving && !blockingLoading) ? <CircularProgress size={24} /> : <Save style={{ fontSize: '1.25rem', color: saveDisabled ? 'grey' : undefined }} />}
                            </IconButton>
                        </Tooltip>
                    </>}
                </>}

                <IconButton
                    edge="end"
                    color="default"
                    onClick={() => setFullscreen(!isFullscreen)}
                    style={{ position: 'absolute', top: 5, right: isFullscreen ? 30 : 20, zIndex }}

                >
                    {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                </IconButton>
            </div>

            {withFilesEditor && <div ref={menuRef} style={{ width: withFilesEditor ? leftMenuWidth : 0 }}>
                {isMobile &&
                    <ClickAwayListener
                        onClickAway={(event) => {
                            setMobileMenu(false);
                        }}
                    >

                    <div style={{ position: "absolute", padding: 5, left: 0, top: 0, cursor: 'pointer' }}>
                    <Avatar
                        onClick={(event) => {
                            setMobileMenu((prev) => !prev)
                        }}
                        variant="outlined" style={{ backgroundColor: 'rgba(0, 0, 0, 0.05)' }}><MenuIcon /></Avatar>
                    </div>
                    </ClickAwayListener>
                    }

                <div style={{ position: "absolute", paddingTop: 5 }}>
                    <FilesEditor
                        {...{ kbId, setFilesData, filesData, filepathVersionsOpen, setFilepathVersionsOpen }}
                        isFullscreen={isFullscreen}
                        selectRight={selectRight}
                        selectLeft={selectLeft}
                        onFileOpen={(fileItem) => onOpenFile({ fileItem, ...fileOpenParams })}
                        onFileDelete={(filepath) => onFileDelete(filepath, { setLoading, updateFiles, setLoadedFiles, Alert })}
                        onFileCreate={({ folder, children, isFolder }) => onFileCreate({ folder, children, isFolder, setBlockingLoading, Alert, handleSave, showPrompt })}
                        onCreateAPI={() => {
                            alert('NEW API (Todo)');
                        }}
                    />
                </div>
            </div>}

            {KBData?.kbTitle && <div style={{
                marginLeft: withFilesEditor ? leftMenuWidth : 0,
                height: ((!loadedFiles?.length || !selectedFile) ? 500 : undefined)
            }}>
                <div style={{ position: "relative" }}>
                    <Suspense fallback={<div></div>}>
                        {loadedFiles.map((file, index) => (
                            <div
                                key={file.Key}
                                style={{ display: file.selected ? 'block' : 'none' }}
                            >
                                <LazyEditor
                                    onMount={(editor, monaco) => handleEditorDidMount(editor, monaco, file.Key, KBData, codeAbortController, setDiffs)}
                                    options={MonacoOptions}
                                    height={isFullscreen ? "90vh" : "60vh"}
                                    defaultLanguage={language ? language : 'text'}
                                    value={file.content}
                                    onChange={(v) => {
                                        if (file) {
                                            const position = editors[file.Key]?.getPosition();
                                            setContentAndCursor(v, position);
                                        }
                                    }}
                                    style={{ paddingTop: isFullscreen ? 60 : 50 }}
                                    language={file ? language : 'text'}
                                    theme={monacoTheme}
                                />
                            </div>
                        ))}
                    </Suspense>
                    {selectedFile?.tabIsLoading && <CodeTabIsLoading isFullscreen={isFullscreen} />}
                </div>
            </div>}

            <CreateCodeFilePrompt
                {...{setFullscreen, isFullscreen, fileItemToCreate}}
                handleClose={() => {
                    if (fileItemToCreate) setLoadedFiles((prev) => prev.filter(o => o.Key !== fileItemToCreate.Key))
                    setFileItemToCreate(null);
                }}
                handleBlueprintSelect={createHandleBlueprintSelect({ handleSave, fileItemToCreate, setFileItemToCreate,
                    fileOpenParams, selectedFile, loadedFiles, setCodeLoading, setResponse, updateFiles, kbId, showPrompt, showConfirm, setBlockingLoading })}
                handleBlankSelect={() => {
                    handleSave({
                        injectedContent: `// Write your ${getFilenameByKey(fileItemToCreate.Key)} handler below:`
                    }).then(() => {
                        onOpenFile({ fileItem: fileItemToCreate, ...fileOpenParams })
                        setFileItemToCreate(null);
                    });
                }}
            />

            <CodeEditorConsole isFullscreen={isFullscreen} onClose={() => setResponse(null)}
                               {...{ mobileMenu, codeLoading: (codeLoading || codeRunLoading), response }} text={ActionText} />
        </div>
    );
}